import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  Link,
  MenuItem,
  Rating,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RefreshIcon from '@mui/icons-material/Refresh';
import StarIcon from '@mui/icons-material/Star';
import { toast } from 'react-toastify';

import useGoogleReview from '../../hooks/useGoogleReview';
import useGoogleBusiness from '../../hooks/useGoogleBusiness';
import AuthContext from '../../context/authContext';

const fmtStars = v =>
  typeof v === 'number'
    ? v.toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 })
    : '—';

// Confidence (0..1) of a deterministic name/address match → chip colour.
const scoreColor = s => (s == null ? 'default' : s >= 0.7 ? 'success' : s >= 0.45 ? 'warning' : 'default');
const pct = s => (typeof s === 'number' ? `${Math.round(s * 100)}%` : '');

const GoogleRatingMatchDialog = ({ open, onClose, onConfirmed }) => {
  const authCtx = useContext(AuthContext);
  const canMatch = authCtx.checkRight('googleBusiness:match');

  const { getEntertainmentRatings, bulkMatchLocations, refreshLocations } = useGoogleReview();
  const { aiSuggestMatches } = useGoogleBusiness();

  // The hook re-creates its functions each render; keep the latest in a ref so
  // the load effect can depend only on `open`.
  const apiRef = useRef();
  apiRef.current = { getEntertainmentRatings, bulkMatchLocations, refreshLocations, aiSuggestMatches };

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [data, setData] = useState(null);
  // entertainmentId -> selected googleLocationName ('' = no mapping)
  const [selections, setSelections] = useState({});
  // entertainmentId -> { googleLocationName, confidence, reason, locationTitle }
  const [aiSuggestions, setAiSuggestions] = useState({});
  const [filter, setFilter] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiRef.current.getEntertainmentRatings();
      setData(res);
      const init = {};
      (res?.items || []).forEach(it => {
        init[it.entertainmentId] = it.googleLocationName || '';
      });
      setSelections(init);
    } catch (_) {
      // toast handled by hook
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) load();
    else {
      setData(null);
      setSelections({});
      setAiSuggestions({});
      setFilter('');
    }
  }, [open, load]);

  const items = useMemo(() => data?.items || [], [data]);
  const locations = useMemo(() => data?.locations || [], [data]);

  // Fast lookup of a location's computed rating for the rating column + options.
  const locationByResource = useMemo(() => {
    const map = new Map();
    locations.forEach(l => map.set(l.resourceName, l));
    return map;
  }, [locations]);

  const sortedItems = useMemo(() => {
    const term = filter.trim().toLowerCase();
    const filtered = term
      ? items.filter(
          it =>
            (it.longName || '').toLowerCase().includes(term) ||
            (it.branchName || '').toLowerCase().includes(term)
        )
      : items;
    return [...filtered].sort(
      (a, b) =>
        (a.branchName || '').localeCompare(b.branchName || '', 'de') ||
        (a.longName || '').localeCompare(b.longName || '', 'de')
    );
  }, [items, filter]);

  // Assignments whose selection differs from what is stored on the entertainment.
  const changedAssignments = useMemo(
    () =>
      items
        .filter(it => (selections[it.entertainmentId] || '') !== (it.googleLocationName || ''))
        .map(it => ({
          entertainmentId: it.entertainmentId,
          googleLocationName: selections[it.entertainmentId] || '',
        })),
    [items, selections]
  );

  const suggestableCount = useMemo(
    () =>
      items.filter(
        it =>
          it.suggestion &&
          it.suggestion.googleLocationName &&
          (selections[it.entertainmentId] || '') !== it.suggestion.googleLocationName
      ).length,
    [items, selections]
  );

  const summary = useMemo(() => {
    const matched = items.filter(it => (selections[it.entertainmentId] || '')).length;
    const rated = items
      .map(it => locationByResource.get(selections[it.entertainmentId] || ''))
      .filter(loc => loc && typeof loc.averageRating === 'number');
    const totalReviews = rated.reduce((sum, loc) => sum + (loc.reviewCount || 0), 0);
    const avg =
      rated.length > 0
        ? rated.reduce((sum, loc) => sum + loc.averageRating, 0) / rated.length
        : null;
    return { matched, unmatched: items.length - matched, avg, totalReviews };
  }, [items, selections, locationByResource]);

  const updateSelection = (entertainmentId, googleLocationName) =>
    setSelections(prev => ({ ...prev, [entertainmentId]: googleLocationName }));

  const applySuggestions = () => {
    setSelections(prev => {
      const next = { ...prev };
      items.forEach(it => {
        const sug = it.suggestion;
        if (sug && sug.googleLocationName && !(next[it.entertainmentId] || '')) {
          next[it.entertainmentId] = sug.googleLocationName;
        }
      });
      return next;
    });
  };

  // AI matching: only ever proposes, never persists. The user reviews the
  // suggested locations and confirms them with Save.
  const handleAiSuggest = async () => {
    setAiLoading(true);
    try {
      const res = await apiRef.current.aiSuggestMatches({ onlyUnmatched: true });
      const map = {};
      (res?.suggestions || []).forEach(s => {
        if (s.googleLocationName) map[s.entertainmentId] = s;
      });
      setAiSuggestions(map);
      const count = Object.keys(map).length;
      toast.success(
        count > 0 ? `${count} KI-Vorschlag${count === 1 ? '' : 'e'} erstellt` : 'Keine KI-Vorschläge gefunden'
      );
    } catch (_) {
      // toast handled by hook
    } finally {
      setAiLoading(false);
    }
  };

  const aiApplicableCount = useMemo(
    () =>
      Object.entries(aiSuggestions).filter(
        ([eid, s]) => s.googleLocationName && (selections[eid] || '') !== s.googleLocationName
      ).length,
    [aiSuggestions, selections]
  );

  const applyAiSuggestions = () => {
    setSelections(prev => {
      const next = { ...prev };
      Object.entries(aiSuggestions).forEach(([eid, s]) => {
        if (s.googleLocationName) next[eid] = s.googleLocationName;
      });
      return next;
    });
  };

  const handleSyncLocations = async () => {
    setSyncing(true);
    try {
      await apiRef.current.refreshLocations();
      toast.success('Google-Standorte aktualisiert');
      await load();
    } catch (_) {
      // toast handled by hook
    } finally {
      setSyncing(false);
    }
  };

  const handleSave = async () => {
    if (changedAssignments.length === 0) {
      toast.info('Keine Änderungen zum Speichern.');
      return;
    }
    setSaving(true);
    try {
      const res = await apiRef.current.bulkMatchLocations(changedAssignments);
      toast.success(
        `${res?.updated ?? changedAssignments.length} Zuordnung${
          (res?.updated ?? changedAssignments.length) === 1 ? '' : 'en'
        } gespeichert`
      );
      await load();
      onConfirmed?.();
    } catch (_) {
      // toast handled by hook
    } finally {
      setSaving(false);
    }
  };

  const locationLabel = loc => {
    const rating =
      typeof loc.averageRating === 'number'
        ? ` — ${fmtStars(loc.averageRating)}★ (${loc.reviewCount || 0})`
        : '';
    return `${loc.title || loc.resourceName}${rating}`;
  };

  const renderRatingCell = it => {
    const loc = locationByResource.get(selections[it.entertainmentId] || '');
    const rating = loc && typeof loc.averageRating === 'number' ? loc.averageRating : null;
    if (rating == null) {
      return (
        <Typography variant='caption' sx={{ color: 'text.disabled' }}>
          keine Bewertungen
        </Typography>
      );
    }
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <Rating value={rating} precision={0.1} readOnly size='small' />
        <Typography variant='body2' sx={{ fontWeight: 600 }}>
          {fmtStars(rating)}
        </Typography>
        <Typography variant='caption' sx={{ color: 'text.secondary' }}>
          ({loc.reviewCount || 0})
        </Typography>
      </Box>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth='lg' fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 6 }}>
        <StarIcon sx={{ color: '#FBBC04' }} />
        Google-Standorte zuordnen
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }} size='small'>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant='body2' sx={{ color: 'text.secondary', mb: 2 }}>
          Ordne jedem Entertainment den passenden Google-Standort (Google-Maps-Eintrag) zu. Diese
          Zuordnung ist die gemeinsame Grundlage für die Google-Rezensionen und den
          Öffnungszeiten-Abgleich. Lass dir per KI oder anhand von Name/Adresse Vorschläge erzeugen,
          prüfe sie und speichere erst, wenn alles passt. Die Bewertungsspalte (aus den
          synchronisierten Rezensionen) dient als zusätzliche Kontrolle.
        </Typography>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Box
              sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 2 }}
            >
              <Button
                variant='contained'
                startIcon={aiLoading ? <CircularProgress size={18} color='inherit' /> : <AutoAwesomeIcon />}
                onClick={handleAiSuggest}
                disabled={!canMatch || aiLoading || locations.length === 0}
              >
                KI-Vorschläge erzeugen
              </Button>
              {aiApplicableCount > 0 && (
                <Button variant='outlined' startIcon={<AutoAwesomeIcon />} onClick={applyAiSuggestions}>
                  {`Alle KI-Vorschläge übernehmen (${aiApplicableCount})`}
                </Button>
              )}
              <Button variant='text' onClick={applySuggestions} disabled={suggestableCount === 0}>
                {`Namens-Vorschläge übernehmen${suggestableCount > 0 ? ` (${suggestableCount})` : ''}`}
              </Button>
              <Button
                variant='outlined'
                startIcon={syncing ? <CircularProgress size={18} /> : <RefreshIcon />}
                onClick={handleSyncLocations}
                disabled={syncing}
              >
                Google-Standorte aktualisieren
              </Button>
              <Box sx={{ flex: 1 }} />
              <TextField
                size='small'
                placeholder='Filtern …'
                defaultValue=''
                onChange={e => setFilter(e.target.value)}
                sx={{ minWidth: 200 }}
              />
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 2 }}>
              <Chip
                size='small'
                color='success'
                variant='outlined'
                label={`${summary.matched} zugeordnet`}
              />
              <Chip
                size='small'
                color={summary.unmatched > 0 ? 'warning' : 'default'}
                variant='outlined'
                label={`${summary.unmatched} ohne Zuordnung`}
              />
              {summary.avg != null && (
                <Chip
                  size='small'
                  icon={<StarIcon sx={{ fontSize: 16, color: '#FBBC04' }} />}
                  label={`Ø ${fmtStars(summary.avg)} über ${summary.totalReviews} Bewertungen`}
                />
              )}
            </Box>

            {locations.length === 0 && (
              <Alert severity='warning' sx={{ mb: 2 }}>
                Es sind keine Google-Standorte vorhanden. Klicke auf „Google-Standorte
                aktualisieren“, um sie aus dem Google-Business-Profil zu laden.
              </Alert>
            )}
            {items.length === 0 && (
              <Alert severity='info' sx={{ mb: 2 }}>
                Keine Entertainments gefunden.
              </Alert>
            )}

            {/* Table header */}
            {sortedItems.length > 0 && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  px: 1,
                  py: 0.5,
                  color: 'text.secondary',
                }}
              >
                <Typography variant='caption' sx={{ flex: '1 1 240px', minWidth: 180, fontWeight: 600 }}>
                  Entertainment
                </Typography>
                <Typography variant='caption' sx={{ flex: '1 1 280px', minWidth: 220, fontWeight: 600 }}>
                  Google-Standort
                </Typography>
                <Typography variant='caption' sx={{ flex: '0 0 200px', fontWeight: 600 }}>
                  Sterne
                </Typography>
              </Box>
            )}

            {sortedItems.map(it => {
              const selected = selections[it.entertainmentId] || '';
              const isChanged = selected !== (it.googleLocationName || '');
              const sug = it.suggestion;
              const ai = aiSuggestions[it.entertainmentId];
              const showAi = ai && ai.googleLocationName && selected !== ai.googleLocationName;
              const showSuggestion = sug && sug.googleLocationName && !selected && !showAi;
              return (
                <Box
                  key={it.entertainmentId}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    px: 1,
                    py: 1,
                    flexWrap: 'wrap',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    bgcolor: isChanged ? 'action.hover' : 'transparent',
                  }}
                >
                  <Box sx={{ flex: '1 1 240px', minWidth: 180 }}>
                    <Typography variant='body2' sx={{ fontWeight: 600 }}>
                      {it.longName}
                    </Typography>
                    <Typography variant='caption' sx={{ color: 'text.secondary' }}>
                      {it.branchName || '—'}
                    </Typography>
                  </Box>

                  <Box
                    sx={{ flex: '1 1 280px', minWidth: 220, display: 'flex', flexDirection: 'column', gap: 0.5 }}
                  >
                    <FormControl size='small' fullWidth>
                      <Select
                        displayEmpty
                        value={selected}
                        onChange={e => updateSelection(it.entertainmentId, e.target.value)}
                        renderValue={val => {
                          if (!val) return <em>— keine Zuordnung —</em>;
                          const loc = locationByResource.get(val);
                          return loc ? loc.title || loc.resourceName : val;
                        }}
                      >
                        <MenuItem value=''>
                          <em>— keine Zuordnung —</em>
                        </MenuItem>
                        {locations.map(loc => (
                          <MenuItem key={loc.resourceName} value={loc.resourceName}>
                            {locationLabel(loc)}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    {showAi && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Tooltip title={ai.reason || 'KI-Vorschlag'}>
                          <Chip
                            size='small'
                            color={scoreColor(ai.confidence)}
                            icon={<AutoAwesomeIcon sx={{ fontSize: 14 }} />}
                            label={`KI: ${ai.locationTitle || ai.googleLocationName}${ai.confidence != null ? ` (${pct(ai.confidence)})` : ''}`}
                            onClick={() => updateSelection(it.entertainmentId, ai.googleLocationName)}
                          />
                        </Tooltip>
                      </Box>
                    )}
                    {showSuggestion && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Tooltip title='Vorschlag anhand von Name & Adresse'>
                          <Chip
                            size='small'
                            variant='outlined'
                            color={scoreColor(sug.score)}
                            icon={<AutoAwesomeIcon sx={{ fontSize: 14 }} />}
                            label={`Vorschlag: ${sug.title}`}
                            onClick={() => updateSelection(it.entertainmentId, sug.googleLocationName)}
                          />
                        </Tooltip>
                      </Box>
                    )}
                  </Box>

                  <Box sx={{ flex: '0 0 200px', display: 'flex', alignItems: 'center', gap: 1 }}>
                    {renderRatingCell(it)}
                    {locationByResource.get(selected)?.googleMapsUri && (
                      <Tooltip title='Auf Google Maps öffnen'>
                        <Link
                          href={locationByResource.get(selected).googleMapsUri}
                          target='_blank'
                          rel='noopener'
                          sx={{ display: 'inline-flex' }}
                        >
                          <OpenInNewIcon fontSize='small' />
                        </Link>
                      </Tooltip>
                    )}
                  </Box>
                </Box>
              );
            })}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color='inherit'>
          Schließen
        </Button>
        <Button
          variant='contained'
          onClick={handleSave}
          disabled={saving || changedAssignments.length === 0}
          startIcon={saving ? <CircularProgress size={18} color='inherit' /> : null}
        >
          {changedAssignments.length > 0
            ? `${changedAssignments.length} Änderung${changedAssignments.length === 1 ? '' : 'en'} speichern`
            : 'Speichern'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GoogleRatingMatchDialog;
